import { ClipboardText, FileText, Stack } from "@phosphor-icons/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { Panel } from "@/components/Panel";
import { SearchInput } from "@/components/SearchInput";
import { SectionHeading } from "@/components/SectionHeading";
import { StatCard } from "@/components/StatCard";
import { type Status, StatusBadge } from "@/components/StatusBadge";
import { TruncatedText } from "@/components/TruncatedText";
import { EmptyState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { canAnywhere } from "@/features/auth/permissions";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { greetingKey, greetingName } from "@/features/dashboard/greeting";
import type { MasterType } from "@/features/master-types/api";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import type { MasterRecord } from "@/features/records/api";
import { recentRecordsQuery, recordCountQuery } from "@/features/records/queries";
import { submissionCountQuery } from "@/features/submissions/queries";

/**
 * The landing page, in sections: how much master data exists, what
 * changed recently, and what is waiting for a decision.
 *
 * DEE-57, on top of DEE-56 (the record-count tiles, kept) and DEE-13's
 * still-missing UI (DEE-55). Nothing here assumes a permission the caller
 * might not hold — the nav item is visible to *every* signed-in user,
 * unlike `/records` or `/submissions`, which are only ever reached by
 * someone who already holds the permission their loader needs.
 *
 * DEE-64 restyled it against the approved design. The data and the
 * permission gates were already right; this is layout and hierarchy.
 */
export const Route = createFileRoute("/_authed/")({
  staticData: { crumbs: [{ key: "nav.dashboard" }] },
  loader: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    if (canAnywhere(me.permissions, "master_type.read")) {
      await context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale));
    }
  },
  component: Dashboard,
});

function recordStatus(row: MasterRecord): Status {
  if (row.status === "approved" && !row.is_active) return "inactive";
  if (row.status === "pending") return "pending";
  if (row.status === "rejected") return "rejected";
  if (row.status === "draft") return "draft";
  return "approved";
}

function typeName(type: MasterType, locale: string, defaultLocale: string): string {
  return type.display_name[locale] ?? type.display_name[defaultLocale] ?? type.code;
}

function Dashboard() {
  const { t } = useTranslation();
  const { me, can, canAnywhere: sessionCanAnywhere } = useSession();
  const locale = me.resolved_locale;
  const defaultLocale = me.tenant.default_locale;

  const canBrowseTypes = sessionCanAnywhere("master_type.read");
  const { data: index } = useQuery({
    ...masterTypeIndexQuery(locale),
    enabled: canBrowseTypes,
  });

  const readableTypes = useMemo(
    () => (index?.all ?? []).filter((type) => can("record.read", type.code)),
    [index, can],
  );

  const hasNothing = !canBrowseTypes && readableTypes.length === 0;
  const canSeeQueue = sessionCanAnywhere("schema.read");

  const greeting = t(greetingKey(new Date().getHours()), {
    name: greetingName(me.display_name, me.email),
  });

  return (
    <>
      <PageHeader
        title={greeting}
        description={t("dashboard.subtitle")}
        actions={
          // Gated the same way the nav items are: a call to action
          // pointing at a route the caller will be refused is worse than
          // no call to action.
          hasNothing ? undefined : (
            <>
              {canSeeQueue && (
                <Button asChild variant="secondary" tooltip={t("tooltips.reviewSubmissions")}>
                  <Link to="/submissions">{t("dashboard.cta.reviewSubmissions")}</Link>
                </Button>
              )}
              {readableTypes.length > 0 && (
                <Button asChild tooltip={t("tooltips.goToRecords")}>
                  <Link to="/records">{t("dashboard.cta.goToRecords")}</Link>
                </Button>
              )}
            </>
          )
        }
      />

      {hasNothing ? (
        // Active, signed in, and holding no grants at all. Distinct from
        // "waiting for activation" — an admin has to grant a role, not
        // flip a status, and telling them to wait would be wrong.
        <EmptyState title={t("auth.noPermissions.title")} body={t("auth.noPermissions.body")} />
      ) : (
        <div className="space-y-8">
          <RecordsSection types={readableTypes} locale={locale} defaultLocale={defaultLocale} />

          {/* Recently updated is the wider column because its rows carry a
              name, a type, a version and a status; approvals carry a
              number. 1.6fr/1fr is the design's ratio, and it collapses to
              stacked below `lg` rather than squeezing both. */}
          <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr] lg:items-start">
            <RecentlyUpdatedSection
              types={readableTypes}
              locale={locale}
              defaultLocale={defaultLocale}
            />
            <ApprovalsSection />
          </div>
        </div>
      )}
    </>
  );
}

/** Skeleton, error text or the number — the three states every count has. */
function CountValue({
  isPending,
  isError,
  value,
  locale,
  className,
}: {
  isPending: boolean;
  isError: boolean;
  value: number | undefined;
  locale: string;
  className?: string;
}) {
  const { t } = useTranslation();
  if (isPending) return <Skeleton className="h-6 w-10" />;
  if (isError) return <span className="text-muted text-sm">{t("dashboard.countUnavailable")}</span>;
  return <span className={className}>{(value ?? 0).toLocaleString(locale)}</span>;
}

function RecordsSection({
  types,
  locale,
  defaultLocale,
}: {
  types: MasterType[];
  locale: string;
  defaultLocale: string;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");

  const counts = useQueries({ queries: types.map((type) => recordCountQuery(type.code)) });

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === "") return types;
    return types.filter(
      (type) =>
        type.code.toLowerCase().includes(needle) ||
        typeName(type, locale, defaultLocale).toLowerCase().includes(needle),
    );
  }, [types, filter, locale, defaultLocale]);

  if (types.length === 0) return null;

  return (
    <section>
      <SectionHeading
        title={t("dashboard.sections.masterData", { count: types.length })}
        help={t("dashboard.about.masterData")}
      />

      {/* Always present, the same way `DataTable`'s search is — not
          gated on a row-count threshold, so it is already there the day
          a tenant's registry grows past a handful of types. */}
      <SearchInput
        label={t("dashboard.filterTypes")}
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder={t("dashboard.filterTypes")}
        containerClassName="mb-3 max-w-sm"
      />

      {filtered.length === 0 ? (
        <p className="text-muted text-sm">{t("table.noResults.title")}</p>
      ) : (
        // Compact rows rather than the old card grid: a registry is a
        // list of types with a number each, and a three-across grid makes
        // the eye scan in two dimensions to compare one.
        <ul className="space-y-2">
          {filtered.map((type) => {
            const query = counts[types.indexOf(type)];
            return (
              <li key={type.id}>
                <StatCard
                  icon={Stack}
                  label={typeName(type, locale, defaultLocale)}
                  subtext={t("dashboard.currentRecords")}
                  to="/records/$code"
                  params={{ code: type.code }}
                  value={
                    <CountValue
                      isPending={query?.isPending ?? true}
                      isError={query?.isError ?? false}
                      value={query?.data}
                      locale={locale}
                    />
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const RECENT_LIMIT = 5;
const RECENT_TOTAL = 8;
const SKELETON_ROWS = ["skeleton-1", "skeleton-2", "skeleton-3"];

function RecentlyUpdatedSection({
  types,
  locale,
  defaultLocale,
}: {
  types: MasterType[];
  locale: string;
  defaultLocale: string;
}) {
  const { t } = useTranslation();

  const recent = useQueries({
    queries: types.map((type) => recentRecordsQuery(type.code, locale, RECENT_LIMIT)),
  });

  const loading = types.length > 0 && recent.some((query) => query.isPending);

  const items = useMemo(
    () =>
      recent
        .flatMap((query, i) => (query.data ?? []).map((record) => ({ record, type: types[i] })))
        .filter(
          (item): item is { record: MasterRecord; type: MasterType } => item.type !== undefined,
        )
        .sort(
          (a, b) =>
            new Date(b.record.created_at).getTime() - new Date(a.record.created_at).getTime(),
        )
        .slice(0, RECENT_TOTAL),
    [recent, types],
  );

  if (types.length === 0) return null;

  return (
    <section>
      <SectionHeading title={t("dashboard.sections.recentlyUpdated")} />

      {loading ? (
        <div className="space-y-2">
          {SKELETON_ROWS.map((key) => (
            <Skeleton key={key} className="h-14 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted text-sm">{t("dashboard.noRecentActivity")}</p>
      ) : (
        <Panel className="overflow-hidden">
          <ul className="divide-y divide-border">
            {items.map(({ record, type }) => (
              // `record_id` (this version's row), not `id` (the lineage) --
              // a creator's own pending edit and the still-current approved
              // version share one lineage id and would collide as a key.
              // Same distinction `records.$code.index.tsx` draws.
              <li key={record.record_id}>
                <Link
                  to="/records/$code/$recordId"
                  params={{ code: type.code, recordId: record.id }}
                  className="flex min-h-14 items-center justify-between gap-3 px-4 py-2.5 hover:bg-surface-muted"
                >
                  <span className="min-w-0">
                    {/* A record's localised display name, in a feed sized
                        to a dashboard column rather than to the name.
                        (DEE-52) */}
                    <TruncatedText
                      className="font-medium text-foreground text-sm"
                      text={record.display_name ?? record.business_key}
                    />
                    {/* Composed here rather than as two nodes: the tooltip
                        reveals the line a reader could not finish, so the
                        line has to exist as one string. */}
                    <TruncatedText
                      className="font-caption text-muted text-xs"
                      text={`${typeName(type, locale, defaultLocale)} · ${t("dashboard.version", {
                        version: record.version,
                      })}`}
                    />
                  </span>
                  <StatusBadge status={recordStatus(record)} className="shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </section>
  );
}

function ApprovalsSection() {
  const { t } = useTranslation();
  const { me, canAnywhere } = useSession();

  // Same gate the `Submissions` nav item uses — a caller who cannot see
  // the queue at all should not see a summary of it either.
  const canSeeQueue = canAnywhere("schema.read");
  const schema = useQuery({ ...submissionCountQuery("schema"), enabled: canSeeQueue });
  const record = useQuery({ ...submissionCountQuery("record"), enabled: canSeeQueue });

  if (!canSeeQueue) return null;

  const tiles = [
    {
      key: "schema" as const,
      icon: FileText,
      label: t("dashboard.approvals.schema"),
      query: schema,
      search: { kind: "schema" as const },
    },
    {
      key: "record" as const,
      icon: ClipboardText,
      label: t("dashboard.approvals.record"),
      query: record,
      search: { kind: "record" as const },
    },
  ];

  return (
    <section>
      <SectionHeading title={t("dashboard.sections.approvals")} />
      <ul className="space-y-2">
        {tiles.map((tile) => (
          <li key={tile.key}>
            <StatCard
              icon={tile.icon}
              label={tile.label}
              subtext={t("dashboard.pendingApproval")}
              to="/submissions"
              search={tile.search}
              value={
                <CountValue
                  isPending={tile.query.isPending}
                  isError={tile.query.isError}
                  value={tile.query.data?.count}
                  locale={me.resolved_locale}
                  // The design puts these numerals in orange. `--accent`
                  // is 2.23:1 on this card and they are large text with a
                  // 3:1 floor, so the darkened token carries it — 6.80:1.
                  // See docs/ui-contract.md.
                  className="text-accent-strong"
                />
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
